import {Accordion, Badge, Col, Form, Row} from 'react-bootstrap'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import {TFormMemberPolicy} from '../../../policies/policiesTypes'

/** Ids estáticos: el extractor de FormatJS no sigue ids guardados en variables. */
const questionLabels = defineMessages({
  hasFelony: {id: 'policies.detail.form.life.hasFelony', defaultMessage: 'Felony'},
  hasBankruptcy: {id: 'policies.detail.form.life.hasBankruptcy', defaultMessage: 'Bankruptcy'},
  hasDui: {id: 'policies.detail.form.life.hasDui', defaultMessage: 'DUI'},
})

const BACKGROUND_QUESTIONS = ['hasFelony', 'hasBankruptcy', 'hasDui'] as const

interface Props {
  eventKey: string
  values: TFormMemberPolicy
  handleChange: (field: string, value: any) => void
}

/**
 * Sección C del Pre-Application Form de Vida — la parte que no existe en ACA.
 *
 * Lugar de nacimiento (`countryBirth`), alien number (`uscis`), licencia de conducir
 * y empleador/ocupación/ingreso ya se editan en los acordeones de Legal documents y
 * Employment, así que acá solo van los campos nuevos.
 */
export const LifeBackgroundAccordionItem: React.FC<Props> = ({eventKey, values, handleChange}) => {
  const intl = useIntl()
  const yesLabel = intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})
  const noLabel = intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})
  const selectPlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })

  const toTriState = (value: boolean | null | undefined) =>
    value === true ? 'true' : value === false ? 'false' : ''

  const fromTriState = (raw: string) => (raw === '' ? null : raw === 'true')

  // Las respuestas en "sí" se resumen como badges en el header, para que el agente
  // vea que hay antecedentes sin tener que abrir el acordeón.
  const affirmative = BACKGROUND_QUESTIONS.filter((field) => values?.[field] === true)
  const hasAnyAffirmative = affirmative.length > 0

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        {/* Mismo layout que Legal information: título en negrita y resumen debajo. */}
        <div style={{width: '100%'}}>
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
              fontSize: '0.95rem',
              lineHeight: '1.2',
            }}
          >
            <FormattedMessage
              id='policies.detail.form.life.backgroundHeading'
              defaultMessage='Background'
            />
          </div>
          <div
            style={{
              fontSize: '0.85rem',
              color: '#666',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 8px',
              alignItems: 'center',
              lineHeight: '1.2',
            }}
          >
            {affirmative.map((field) => (
              <Badge key={field} bg='warning' className='text-dark' style={{fontSize: '0.7rem', padding: '2px 6px'}}>
                {intl.formatMessage(questionLabels[field])}
              </Badge>
            ))}
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <Row>
          {BACKGROUND_QUESTIONS.map((field) => (
            <Col md={4} key={field}>
              <Form.Group>
                <Form.Label>{intl.formatMessage(questionLabels[field])}</Form.Label>
                <Form.Select
                  value={toTriState(values?.[field] as boolean | null)}
                  onChange={(e) => handleChange(field, fromTriState(e.target.value))}
                >
                  <option value=''>{selectPlaceholder}</option>
                  <option value='true'>{yesLabel}</option>
                  <option value='false'>{noLabel}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          ))}
        </Row>

        {hasAnyAffirmative && (
          <Row className='mt-3'>
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='policies.detail.form.life.backgroundExplanation'
                    defaultMessage='Explain'
                  />
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={values?.backgroundExplanation || ''}
                  onChange={(e) => handleChange('backgroundExplanation', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </Accordion.Body>
    </Accordion.Item>
  )
}
